import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../services/api';

// Product interfaces based on the new database schema
export interface Product {
  id: number;
  name: string;
  category: string;
  unit_type: 'weight' | 'volume' | 'count' | 'pallets' | 'mixed';
  weight_per_unit_kg?: number;
  volume_per_unit_m3?: number;
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  stackable: boolean;
  requires_refrigeration: boolean;
  hazmat_class?: string;
  handling_instructions?: string;
  project_id: number;
  created_at: string;
  updated_at: string;
}

export interface ProductCreate {
  name: string;
  category?: string;
  unit_type: 'weight' | 'volume' | 'count' | 'pallets' | 'mixed';
  weight_per_unit_kg?: number;
  volume_per_unit_m3?: number;
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  stackable?: boolean;
  requires_refrigeration?: boolean;
  hazmat_class?: string;
  handling_instructions?: string;
}

export interface ProductUpdate {
  name?: string;
  category?: string;
  unit_type?: 'weight' | 'volume' | 'count' | 'pallets' | 'mixed';
  weight_per_unit_kg?: number;
  volume_per_unit_m3?: number;
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  stackable?: boolean;
  requires_refrigeration?: boolean;
  hazmat_class?: string;
  handling_instructions?: string;
}

export interface CapacityDefinition {
  id: number;
  name: string;
  unit: 'kg' | 'm3' | 'pallets' | 'items' | 'liters' | 'tons';
  dimension_index: number;
  description?: string;
  is_active: boolean;
  project_id: number;
  created_at: string;
}

export interface JobProduct {
  id: number;
  job_id: number;
  product_id: number;
  quantity: number;
  calculated_weight?: number;
  calculated_volume?: number;
  capacity_requirements: number[];
  product: Product;
  created_at: string;
  updated_at: string;
}

export interface JobProductAssignment {
  product_id: number;
  quantity: number;
}

export interface ProductListResponse {
  products: Product[];
  total_count: number;
  pagination: {
    limit: number;
    offset: number;
    has_more: boolean;
  };
}

export interface CapacityCalculationResponse {
  success: boolean;
  calculated_capacity: number[];
  capacity_breakdown: Array<{
    product_name: string;
    quantity: number;
    weight_per_unit_kg: number;
    volume_per_unit_m3: number;
    calculated_weight: number;
    calculated_volume: number;
    capacity_requirements: number[];
  }>;
  total_weight_kg?: number;
  total_volume_m3?: number;
  validation_warnings: string[];
}

// ===============================================
// PRODUCTS HOOKS
// ===============================================

/**
 * Hook to get all products with filtering and search
 */
export const useProducts = (
  projectId: number,
  options?: {
    category?: string;
    unit_type?: string;
    requires_refrigeration?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
    sort_by?: 'name' | 'category' | 'created_at';
    sort_order?: 'asc' | 'desc';
  }
) => {
  return useQuery<ProductListResponse, Error>({
    queryKey: ['products', projectId, options],
    queryFn: async () => {
      try {
        const params = new URLSearchParams({
          project_id: projectId.toString(),
          ...(options?.category && { category: options.category }),
          ...(options?.unit_type && { unit_type: options.unit_type }),
          ...(options?.requires_refrigeration !== undefined && { 
            requires_refrigeration: options.requires_refrigeration.toString() 
          }),
          ...(options?.search && { search: options.search }),
          ...(options?.limit && { limit: options.limit.toString() }),
          ...(options?.offset && { offset: options.offset.toString() }),
          ...(options?.sort_by && { sort_by: options.sort_by }),
          ...(options?.sort_order && { sort_order: options.sort_order }),
        });

        // Use APIClient's callEdgeFunction method for proper CORS handling
        const result = await (apiClient as any).callEdgeFunction(`products?${params}`);
        
        // Handle both direct array response and wrapped response
        if (Array.isArray(result)) {
          return {
            products: result,
            total_count: result.length,
            pagination: {
              limit: options?.limit || 50,
              offset: options?.offset || 0,
              has_more: false
            }
          };
        }
        
        return result;
      } catch (error) {
        console.error('Error fetching products:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 3;
    },
  });
};

/**
 * Hook to get a single product by ID
 */
export const useProduct = (productId: number) => {
  return useQuery<Product, Error>({
    queryKey: ['product', productId],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/products/${productId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Product not found');
          throw new Error(`Failed to fetch product: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error fetching product:', error);
        throw error;
      }
    },
    enabled: !!productId && productId > 0,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook to create a new product
 */
export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Product, Error, { product: ProductCreate; projectId: number }>({
    mutationFn: async ({ product, projectId }) => {
      try {
        const params = new URLSearchParams({ project_id: projectId.toString() });
        
        // Use APIClient's callEdgeFunction method for proper CORS handling
        const result = await (apiClient as any).callEdgeFunction(`products?${params}`, {
          method: 'POST',
          body: JSON.stringify(product)
        });
        
        return result;
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to create product');
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate products list to refetch with new product
      queryClient.invalidateQueries({ queryKey: ['products', variables.projectId] });
      // Add the new product to the cache
      queryClient.setQueryData(['product', data.id], data);
    },
    onError: (error) => {
      console.error('Error creating product:', error);
    },
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook to update a product
 */
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation<Product, Error, { id: number; updates: ProductUpdate }>({
    mutationFn: async ({ id, updates }) => {
      try {
        const response = await fetch(`/api/products/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          },
          body: JSON.stringify(updates)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Product not found');
          if (response.status === 422) {
            throw new Error(errorData?.detail || 'Validation error: Please check your input');
          }
          throw new Error(errorData?.detail || `Failed to update product: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to update product');
      }
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['product', id] });
      const previousProduct = queryClient.getQueryData(['product', id]);
      return { previousProduct };
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['product', variables.id], data);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error, variables, context) => {
      if (context?.previousProduct) {
        queryClient.setQueryData(['product', variables.id], context.previousProduct);
      }
      console.error('Error updating product:', error);
    },
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook to delete a product
 */
export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, number>({
    mutationFn: async (productId: number) => {
      try {
        const response = await fetch(`/api/products/${productId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Product not found');
          if (response.status === 409) throw new Error('Cannot delete product: It is currently in use');
          throw new Error(errorData?.detail || `Failed to delete product: ${response.status}`);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to delete product');
      }
    },
    onMutate: async (productId) => {
      await queryClient.cancelQueries({ queryKey: ['product', productId] });
      await queryClient.cancelQueries({ queryKey: ['products'] });
    },
    onSuccess: (_, productId) => {
      queryClient.removeQueries({ queryKey: ['product', productId] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (error) => {
      console.error('Error deleting product:', error);
    },
    retry: false,
  });
};

// ===============================================
// CAPACITY DEFINITIONS HOOKS
// ===============================================

/**
 * Hook to get capacity definitions for a project
 */
export const useCapacityDefinitions = (projectId: number) => {
  return useQuery<CapacityDefinition[], Error>({
    queryKey: ['capacity-definitions', projectId],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/products/capacity-definitions?project_id=${projectId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          throw new Error(`Failed to fetch capacity definitions: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error fetching capacity definitions:', error);
        throw error;
      }
    },
    enabled: !!projectId && projectId > 0,
    staleTime: 10 * 60 * 1000, // 10 minutes - capacity definitions change rarely
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

// ===============================================
// JOB PRODUCTS HOOKS
// ===============================================

/**
 * Hook to assign products to a job and get capacity calculation
 */
export const useAssignJobProducts = () => {
  const queryClient = useQueryClient();
  
  return useMutation<CapacityCalculationResponse, Error, { jobId: number; products: JobProductAssignment[] }>({
    mutationFn: async ({ jobId, products }) => {
      try {
        const response = await fetch(`/api/products/jobs/${jobId}/products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          },
          body: JSON.stringify(products)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Job not found');
          if (response.status === 422) {
            throw new Error(errorData?.detail || 'Invalid product assignment data');
          }
          throw new Error(errorData?.detail || `Failed to assign products: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to assign products');
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['job-products', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
    },
    onError: (error) => {
      console.error('Error assigning products to job:', error);
    },
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook to get products assigned to a job
 */
export const useJobProducts = (jobId: number) => {
  return useQuery<JobProduct[], Error>({
    queryKey: ['job-products', jobId],
    queryFn: async () => {
      try {
        const response = await fetch(`/api/products/jobs/${jobId}/products`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Job not found');
          throw new Error(`Failed to fetch job products: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        console.error('Error fetching job products:', error);
        throw error;
      }
    },
    enabled: !!jobId && jobId > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

/**
 * Hook to remove a product from a job
 */
export const useRemoveJobProduct = () => {
  const queryClient = useQueryClient();
  
  return useMutation<CapacityCalculationResponse, Error, { jobId: number; productId: number }>({
    mutationFn: async ({ jobId, productId }) => {
      try {
        const response = await fetch(`/api/products/jobs/${jobId}/products/${productId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('supabase.auth.token')}`
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          if (response.status === 401) throw new Error('Authentication required');
          if (response.status === 403) throw new Error('Access denied');
          if (response.status === 404) throw new Error('Product assignment not found');
          throw new Error(errorData?.detail || `Failed to remove product: ${response.status}`);
        }
        
        return await response.json();
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error('Network error: Unable to remove product');
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['job-products', variables.jobId] });
      queryClient.invalidateQueries({ queryKey: ['job', variables.jobId] });
    },
    onError: (error) => {
      console.error('Error removing product from job:', error);
    },
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });
};

// ===============================================
// UTILITY FUNCTIONS
// ===============================================

/**
 * Validate product data before submission
 */
export const validateProduct = (product: ProductCreate): string[] => {
  const errors: string[] = [];
  
  if (!product.name.trim()) {
    errors.push('Product name is required');
  }
  
  if (product.unit_type === 'weight' && !product.weight_per_unit_kg) {
    errors.push('Weight per unit is required for weight-based products');
  }
  
  if (product.unit_type === 'volume' && !product.volume_per_unit_m3) {
    errors.push('Volume per unit is required for volume-based products');
  }
  
  if (product.weight_per_unit_kg && product.weight_per_unit_kg < 0) {
    errors.push('Weight per unit must be positive');
  }
  
  if (product.volume_per_unit_m3 && product.volume_per_unit_m3 < 0) {
    errors.push('Volume per unit must be positive');
  }
  
  if (product.hazmat_class && !/^[1-9](\.[1-9])?$/.test(product.hazmat_class)) {
    errors.push('Invalid hazmat classification format (use UN format like "1.1", "3", "8")');
  }
  
  return errors;
};

/**
 * Calculate preview capacity for product quantities
 */
export const calculateCapacityPreview = (
  product: Product,
  quantity: number,
  capacityDefinitions: CapacityDefinition[]
): number[] => {
  const result: number[] = [];
  
  capacityDefinitions.forEach((def) => {
    let value = 0;
    
    switch (def.unit) {
      case 'kg':
        value = quantity * (product.weight_per_unit_kg || 0);
        break;
      case 'm3':
        value = quantity * (product.volume_per_unit_m3 || 0);
        break;
      case 'items':
        value = quantity;
        break;
      case 'pallets':
        value = Math.ceil(quantity / 48); // Standard pallet calculation
        break;
      case 'liters':
        value = quantity * (product.volume_per_unit_m3 || 0) * 1000;
        break;
      case 'tons':
        value = quantity * (product.weight_per_unit_kg || 0) / 1000;
        break;
      default:
        value = quantity;
    }
    
    result[def.dimension_index] = value;
  });
  
  return result;
};